import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Scissors, Plus, X, Loader2 } from "lucide-react";
import { timeEntriesApi, mattersApi } from "../../services/api";
import PremiumModal from "../ui/PremiumModal";
import SearchableSelect, {
  SelectOption,
} from "../ui/SearchableSelect";

interface SplitRow {
  matterId: string | null;
  minutes: number;
  narrative: string;
}

interface Props {
  entry: {
    _id: string;
    narrative: string;
    minutes: number;
    rateCents: number;
    amountCents: number;
    matterId?: any;
  };
  onClose: () => void;
  onSplit: () => void;
}

const PALETTE = [
  "#0F5132",
  "#198754",
  "#0d6efd",
  "#6f42c1",
  "#d63384",
  "#fd7e14",
  "#20c997",
  "#0dcaf0",
  "#6c757d",
  "#ffc107",
];

const formatCurrency = (cents: number) =>
  new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(cents / 100);

const SplitEntryModal: React.FC<Props> = ({ entry, onClose, onSplit }) => {
  const [matterOptions, setMatterOptions] = useState<SelectOption[]>([]);
  const [rows, setRows] = useState<SplitRow[]>(() => {
    const halfFloor = Math.floor(entry.minutes / 2);
    const halfCeil = entry.minutes - halfFloor;
    return [
      { matterId: null, minutes: halfCeil, narrative: entry.narrative },
      { matterId: null, minutes: halfFloor, narrative: "" },
    ];
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchMatters = useCallback(async () => {
    try {
      const res = await mattersApi.list({ limit: 200, status: "open" });
      const matters = res.data?.data ?? res.data ?? [];
      setMatterOptions(
        matters.map((m: any) => ({
          value: m._id,
          label: m.title || m.name || m._id,
        }))
      );
    } catch (err) {
      console.error("Failed to load matters", err);
    }
  }, []);

  useEffect(() => {
    fetchMatters();
  }, [fetchMatters]);

  /* ── Derived values ──────────────────────────────────────────── */
  const totalAllocated = useMemo(
    () => rows.reduce((sum, r) => sum + (r.minutes || 0), 0),
    [rows]
  );
  const remaining = entry.minutes - totalAllocated;

  const allValid = useMemo(
    () =>
      remaining === 0 &&
      rows.every((r) => r.matterId && r.narrative.trim() && r.minutes > 0),
    [remaining, rows]
  );

  /* ── Row helpers ─────────────────────────────────────────────── */
  const updateRow = (index: number, patch: Partial<SplitRow>) => {
    setRows((prev) =>
      prev.map((r, i) => (i === index ? { ...r, ...patch } : r))
    );
  };

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      { matterId: null, minutes: 0, narrative: "" },
    ]);
  };

  const removeRow = (index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  /* ── Submit ──────────────────────────────────────────────────── */
  const handleSubmit = async () => {
    if (!allValid) return;
    setSubmitting(true);
    try {
      await timeEntriesApi.splitEntry(entry._id, {
        splits: rows.map((r) => ({
          matterId: r.matterId!,
          minutes: r.minutes,
          narrative: r.narrative.trim(),
        })),
      });
      onSplit();
    } catch (err) {
      console.error("Failed to split entry", err);
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Allocation bar colors ───────────────────────────────────── */
  const barSegments = useMemo(() => {
    if (entry.minutes === 0) return [];
    return rows.map((r, i) => ({
      widthPercent: Math.max(0, (r.minutes / entry.minutes) * 100),
      color: PALETTE[i % PALETTE.length],
    }));
  }, [rows, entry.minutes]);

  /* ── Truncated narrative ─────────────────────────────────────── */
  const truncatedNarrative =
    entry.narrative.length > 120
      ? entry.narrative.slice(0, 120) + "..."
      : entry.narrative;

  /* ── Footer ──────────────────────────────────────────────────── */
  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button
        onClick={onClose}
        className="rounded-xl px-5 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
      >
        Cancel
      </button>
      <button
        disabled={!allValid || submitting}
        onClick={handleSubmit}
        className="inline-flex items-center gap-2 rounded-xl bg-[#0F5132] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#146C43] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
        Split Entry
      </button>
    </div>
  );

  return (
    <PremiumModal
      open
      onClose={onClose}
      title="Split Time Entry"
      icon={Scissors}
      size="lg"
      footer={footer}
    >
      {/* Original entry summary */}
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 mb-5">
        <p className="text-sm text-slate-700 mb-2">{truncatedNarrative}</p>
        <div className="flex items-center gap-4 text-xs text-slate-500">
          <span>
            Total:{" "}
            <span className="font-semibold text-slate-700">
              {Math.floor(entry.minutes / 60)}h {entry.minutes % 60}m
            </span>
          </span>
          <span>
            Amount:{" "}
            <span className="font-semibold text-slate-700">
              {formatCurrency(entry.amountCents)}
            </span>
          </span>
        </div>
      </div>

      {/* Visual allocation bar */}
      <div className="mb-5">
        <div className="flex items-center justify-between text-xs text-slate-500 mb-1.5">
          <span>Allocation</span>
          <span
            className={`font-semibold ${
              remaining < 0
                ? "text-red-600"
                : remaining === 0
                ? "text-emerald-600"
                : "text-slate-600"
            }`}
          >
            Remaining: {remaining} min
          </span>
        </div>
        <div className="flex h-3 w-full overflow-hidden rounded-full bg-slate-200">
          {barSegments.map((seg, i) => (
            <div
              key={i}
              className="transition-all duration-300"
              style={{
                width: `${seg.widthPercent}%`,
                backgroundColor: seg.color,
              }}
            />
          ))}
        </div>
      </div>

      {/* Split rows */}
      <div className="space-y-4 mb-4">
        {rows.map((row, index) => (
          <div
            key={index}
            className="rounded-xl border border-slate-200 bg-white p-4"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div
                  className="h-3 w-3 rounded-full"
                  style={{
                    backgroundColor: PALETTE[index % PALETTE.length],
                  }}
                />
                <span className="text-xs font-semibold text-slate-600">
                  Split {index + 1}
                </span>
              </div>
              {rows.length > 2 && (
                <button
                  onClick={() => removeRow(index)}
                  className="rounded-lg p-1 text-slate-400 hover:bg-red-50 hover:text-red-500 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-[1fr_100px] gap-3 mb-3">
              <SearchableSelect
                options={matterOptions}
                value={row.matterId}
                onChange={(val) => updateRow(index, { matterId: val })}
                placeholder="Select matter..."
                searchPlaceholder="Search matters..."
                required
              />
              <div>
                <label className="block text-xs text-slate-500 mb-1">
                  Minutes
                </label>
                <input
                  type="number"
                  min={1}
                  value={row.minutes || ""}
                  onChange={(e) =>
                    updateRow(index, {
                      minutes: Math.max(0, parseInt(e.target.value) || 0),
                    })
                  }
                  className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-600 transition-colors"
                />
              </div>
            </div>

            <textarea
              rows={1}
              value={row.narrative}
              onChange={(e) => updateRow(index, { narrative: e.target.value })}
              placeholder="Narrative..."
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm resize-none focus:outline-none focus:border-emerald-600 transition-colors"
              required
            />
          </div>
        ))}
      </div>

      {/* Add split button */}
      <button
        onClick={addRow}
        className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-medium text-[#0F5132] hover:bg-emerald-50 transition-colors"
      >
        <Plus className="h-4 w-4" />
        Add Split
      </button>
    </PremiumModal>
  );
};

export default SplitEntryModal;
